import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { useDxfLayerPresets, useEditRouteVersion, useReprojectRouteVersion } from "../hooks";
import type { RouteVersion } from "../types";

const isDxf = (f: File) => f.name.toLowerCase().endsWith(".dxf");

// Same list the backend accepts (apps.geo.services.dxf_reproject.SUPPORTED_SRIDS).
const EPSG_OPTIONS = [
  { value: 2180, label: "EPSG:2180 — PUWG 1992 (cała Polska)" },
  { value: 2176, label: "EPSG:2176 — PUWG 2000 strefa 5 (15°E)" },
  { value: 2177, label: "EPSG:2177 — PUWG 2000 strefa 6 (18°E)" },
  { value: 2178, label: "EPSG:2178 — PUWG 2000 strefa 7 (21°E)" },
  { value: 2179, label: "EPSG:2179 — PUWG 2000 strefa 8 (24°E)" },
  { value: 4326, label: "EPSG:4326 — WGS84 (stopnie)" },
  { value: 3857, label: "EPSG:3857 — Web Mercator" },
];

interface Props {
  version: RouteVersion;
  connectionId: string;
  /** Strefa PUWG projektu (connection.srid) — sugerowany układ docelowy konwersji. */
  projectSrid: number;
  onClose: () => void;
  // Called with a task id when the edit triggered a background reparse, so the map tab
  // streams the live log in its existing "Przetwarzanie" panel instead of this modal
  // growing its own.
  onTask: (taskId: string) => void;
}

/**
 * "Edytuj wersję trasy" — the same inputs as the upload form, applied to an existing
 * version in place. Wariant and numer are shown read-only: they carry the
 * unique_together(variant, number) + is_current invariants, so moving a version between
 * variants is not an edit but a re-upload. Everything the parser derives (geometria,
 * długość, obiekty, działki) is recomputed server-side, never typed in here.
 */
export function EditRouteVersionModal({ version, connectionId, projectSrid, onClose, onTask }: Props) {
  const { data: presets } = useDxfLayerPresets();
  const edit = useEditRouteVersion(connectionId);
  const reproject = useReprojectRouteVersion(connectionId);

  const activePresets = (presets ?? []).filter(
    // The version's own preset stays selectable even if it was later deactivated —
    // otherwise opening the modal would silently blank the field and a save meant for
    // the comment would drop the preset and trigger a reparse.
    (p) => p.is_active || p.id === version.layer_preset,
  );
  const [layerPresetId, setLayerPresetId] = useState(version.layer_preset ?? "");
  const [dxfFile, setDxfFile] = useState<File | null>(null);
  const [baseMapFile, setBaseMapFile] = useState<File | null>(null);
  const [comment, setComment] = useState(version.upload_comment ?? "");
  const [error, setError] = useState<string | null>(null);

  // EPSG conversion mode: reprojects the CURRENT stored DXF server-side, so it is
  // mutually exclusive with the other edits (a freshly chosen file/preset would be
  // silently ignored by the reproject endpoint — gray them out instead).
  const [changeEpsg, setChangeEpsg] = useState(false);
  const [sourceSrid, setSourceSrid] = useState(2180);
  const [targetSrid, setTargetSrid] = useState(projectSrid);

  const dxfInvalid = !!dxfFile && !isDxf(dxfFile);
  const presetChanged = layerPresetId !== (version.layer_preset ?? "");
  // Both inputs feed the parser, so either one invalidates the recognised route.
  const willReparse = !!dxfFile || presetChanged;
  const dirty = changeEpsg
    ? sourceSrid !== targetSrid
    : willReparse || !!baseMapFile || comment !== (version.upload_comment ?? "");
  const pending = edit.isPending || reproject.isPending;

  const save = () => {
    if (!dirty || (!changeEpsg && dxfInvalid)) return;
    setError(null);
    if (changeEpsg) {
      reproject.mutate(
        { versionId: version.id, sourceSrid, targetSrid },
        {
          onSuccess: (r) => {
            onTask(r.task_id);
            onClose();
          },
          onError: (e) =>
            setError(
              (e as { response?: { data?: { errors?: { detail?: string } } } })
                ?.response?.data?.errors?.detail ||
                "Nie udało się rozpocząć konwersji układu współrzędnych.",
            ),
        },
      );
      return;
    }
    edit.mutate(
      {
        versionId: version.id,
        dxf: dxfFile,
        baseMap: baseMapFile,
        // Send only what changed: an unchanged preset must not look like an edit and
        // kick off a needless reparse of the whole map.
        layerPreset: presetChanged ? layerPresetId || null : undefined,
        comment: comment !== (version.upload_comment ?? "") ? comment : undefined,
      },
      {
        onSuccess: (r) => {
          if (r && typeof r === "object" && "task_id" in r) onTask(r.task_id);
          onClose();
        },
        onError: (e) =>
          setError(
            (e as { response?: { data?: { errors?: { detail?: string } } } })
              ?.response?.data?.errors?.detail ||
              "Nie udało się zapisać zmian w wersji trasy.",
          ),
      },
    );
  };

  return (
    <Modal open onClose={onClose} title={`Edytuj wersję ${version.label}`} size="md">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 p-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-xs">
          <div>
            <span className="section-label">Wersja</span>
            <p className="font-mono mt-0.5">{version.label}</p>
          </div>
          <div>
            <span className="section-label">Długość trasy</span>
            <p className="font-mono mt-0.5">
              {version.length_m ? `${Math.round(version.length_m)} m` : "—"}
            </p>
          </div>
          <div className="col-span-2 text-[11px] text-[var(--color-text-muted)] leading-snug">
            Wariant i numer wersji wynikają z historii projektu — żeby przenieść trasę do
            innego wariantu, wgraj ją tam jako nową wersję.
          </div>
        </div>

        <div className="p-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] space-y-2">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={changeEpsg}
              onChange={(e) => setChangeEpsg(e.target.checked)}
            />
            <span className="section-label">Zmień układ współrzędnych (EPSG) pliku DXF</span>
          </label>
          {changeEpsg && (
            <>
              <p className="text-[11px] text-[var(--color-text-muted)] leading-snug">
                Konwersja przepisze obecny plik DXF punkt po punkcie do nowego układu —
                dla map dostarczonych w innym układzie niż strefa projektu (np. eksport z
                przetargu w EPSG:2180).
              </p>
              <label className="block">
                <span className="section-label">Z układu</span>
                <select
                  className="input-base mt-1"
                  value={sourceSrid}
                  onChange={(e) => setSourceSrid(Number(e.target.value))}
                >
                  {EPSG_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="section-label">Na układ</span>
                <select
                  className="input-base mt-1"
                  value={targetSrid}
                  onChange={(e) => setTargetSrid(Number(e.target.value))}
                >
                  {EPSG_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                      {o.value === projectSrid ? " — układ projektu" : ""}
                    </option>
                  ))}
                </select>
              </label>
              {sourceSrid === targetSrid && (
                <p className="text-[11px] font-mono text-[var(--color-primary)]">
                  Układ źródłowy i docelowy są takie same — wybierz różne układy.
                </p>
              )}
              {targetSrid !== projectSrid && (
                <p className="text-[11px] font-mono text-amber-600">
                  Układ docelowy różni się od układu projektu (EPSG:{projectSrid}) — trasa
                  może zostać rozpoznana w niewłaściwym miejscu na mapie.
                </p>
              )}
            </>
          )}
        </div>

        <div className={changeEpsg ? "opacity-50 pointer-events-none select-none space-y-4" : "space-y-4"}>
        <label className="block">
          <span className="section-label">Preset warstw mapy</span>
          {activePresets.length > 0 ? (
            <>
              <select
                className="input-base mt-1"
                value={layerPresetId}
                onChange={(e) => setLayerPresetId(e.target.value)}
              >
                <option value="">— brak presetu —</option>
                {activePresets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.line_count + p.object_count} warstw)
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-[var(--color-text-muted)] leading-snug mt-1">
                Wg tego presetu system rozpoznaje warstwy w DXF.{" "}
                <Link
                  to="/power-projects/presety"
                  className="text-[var(--color-primary)] hover:underline"
                >
                  Zarządzaj presetami
                </Link>
              </p>
            </>
          ) : (
            <p className="text-[11px] font-mono text-amber-600 mt-1">
              Brak presetów warstw.{" "}
              <Link to="/power-projects/presety" className="underline">
                Dodaj preset
              </Link>
              .
            </p>
          )}
        </label>

        <div>
          <span className="section-label">Plik DXF</span>
          <p className="text-[11px] text-[var(--color-text-muted)] leading-snug mb-1.5">
            Obecny:{" "}
            <span className="font-mono">
              {version.dxf_file?.original_filename ?? "brak"}
            </span>
            . Wgraj nowy plik tylko wtedy, gdy chcesz go podmienić.
          </p>
          <FileDropZone
            files={dxfFile ? [dxfFile] : []}
            onFilesChange={(f) => setDxfFile(f[0] ?? null)}
            accept=".dxf"
          />
          {dxfInvalid && (
            <p className="text-[11px] font-mono text-[var(--color-primary)] mt-1">
              Wybierz plik DXF (rozszerzenie .dxf).
            </p>
          )}
        </div>

        <div>
          <span className="section-label">Mapa zasadnicza (opcjonalnie)</span>
          <p className="text-[11px] text-[var(--color-text-muted)] leading-snug mb-1.5">
            Obecna:{" "}
            <span className="font-mono">
              {version.base_map_file?.original_filename ?? "brak"}
            </span>
            .
          </p>
          <FileDropZone
            files={baseMapFile ? [baseMapFile] : []}
            onFilesChange={(f) => setBaseMapFile(f[0] ?? null)}
          />
        </div>

        <label className="block">
          <span className="section-label">Komentarz autora</span>
          <textarea
            className="input-base mt-1 text-xs"
            rows={2}
            maxLength={500}
            placeholder="np. korekta trasy po uwagach gestora"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </label>
        </div>

        {(changeEpsg ? sourceSrid !== targetSrid : willReparse) && (
          <div className="flex items-start gap-2 p-2 border border-amber-300 bg-amber-50 dark:bg-amber-500/10 text-[11px] leading-snug">
            <AlertTriangle size={14} className="shrink-0 mt-0.5 text-amber-600" />
            <span>
              {changeEpsg
                ? `Konwersja EPSG:${sourceSrid} → EPSG:${targetSrid} podmieni plik DXF i spowoduje ponowne rozpoznanie`
                : `Zmiana ${dxfFile ? "pliku DXF" : "presetu"} spowoduje ponowne rozpoznanie`}{" "}
              mapy: trasa, długość, obiekty i podgląd zostaną wyliczone od nowa, a
              działki pod trasą przeliczone z ULDK. Postęp zobaczysz w panelu
              „Przetwarzanie".
            </span>
          </div>
        )}

        {version.updated_by_name && (
          <p className="text-[10px] font-mono text-[var(--color-text-muted)]">
            Ostatnia edycja: {version.updated_by_name} ·{" "}
            {new Date(version.updated_at).toLocaleString("pl-PL")}
          </p>
        )}

        {error && (
          <p className="text-[11px] font-mono text-[var(--color-primary)]">{error}</p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Anuluj
          </Button>
          <Button
            disabled={!dirty || (!changeEpsg && dxfInvalid)}
            loading={pending}
            onClick={save}
          >
            {changeEpsg ? "Konwertuj układ" : "Zapisz zmiany"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
